/**
 * Push Notification Service (Capacitor) — app native PARTICULIER.
 *
 * Sécurité web : ce module est conçu pour n'avoir AUCUN effet sur le web.
 * - `@capacitor/push-notifications` est chargé par import dynamique, uniquement
 *   quand la plateforme est native (`Capacitor.isNativePlatform()`). Webpack le
 *   place donc dans un chunk séparé jamais téléchargé par le navigateur.
 * - Toutes les fonctions publiques retournent tôt si `!isPushSupported()`.
 *
 * - Android : token FCM via l'événement Capacitor `registration`.
 * - iOS : token FCM via le plugin natif `FcmToken` (Capacitor Push renvoie le
 *   token APNs, incompatible avec FCM côté serveur). On attend l'événement
 *   `registration` (fin APNs) puis on laisse 3s à Firebase pour l'échange
 *   APNs→FCM avant d'appeler `FcmToken.getToken()`.
 */

import { Capacitor } from '@capacitor/core';
import type {
  Token,
  PushNotificationActionPerformed,
  PushNotificationSchema,
  PushNotificationsPlugin,
} from '@capacitor/push-notifications';
import { buildApiUrl } from '../config/apiConfig';
import { FcmToken } from '../plugins/fcmTokenPlugin';

let listenersAttached = false;
let apnsRegistrationResolver: (() => void) | null = null;
/** Après login / activation : forcer l'envoi du token FCM au backend même si inchangé (Android). */
let pendingAndroidForceSendToken = false;

/** Handler de navigation injecté par l'app (React Router). Fallback: window.location. */
type PushNavigate = (path: string) => void;
let navigateHandler: PushNavigate | null = null;

/** Permet à l'app native de router les taps de notification via React Router. */
export function setPushNavigateHandler(handler: PushNavigate | null): void {
  navigateHandler = handler;
}

export const isPushSupported = (): boolean => Capacitor.isNativePlatform();

/** Import dynamique du plugin push — jamais exécuté côté web. */
let pushPluginPromise: Promise<PushNotificationsPlugin> | null = null;
async function getPush(): Promise<PushNotificationsPlugin> {
  if (!pushPluginPromise) {
    pushPluginPromise = import('@capacitor/push-notifications').then(
      (m) => m.PushNotifications,
    );
  }
  return pushPluginPromise;
}

const getDeviceType = (): 'ios' | 'android' | 'web' => {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return 'ios';
  if (platform === 'android') return 'android';
  return 'web';
};

const navigateTo = (path: string): void => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (navigateHandler) {
    navigateHandler(normalized);
    return;
  }
  window.location.href = normalized;
};

const deactivateTokenOnBackend = async (
  fcmToken: string,
  authToken: string,
): Promise<boolean> => {
  try {
    const response = await fetch(buildApiUrl('/notifications/deactivate-token'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ fcm_token: fcmToken }),
    });
    if (response.ok) return true;
    console.warn('[Push] Échec désactivation token:', response.status);
    return false;
  } catch (error) {
    console.warn('[Push] Erreur réseau désactivation token:', error);
    return false;
  }
};

const sendTokenToBackend = async (fcmToken: string): Promise<boolean> => {
  try {
    const authToken = localStorage.getItem('token');
    if (!authToken) {
      console.warn('[Push] Pas de JWT, enregistrement token impossible');
      return false;
    }
    const response = await fetch(buildApiUrl('/notifications/register-push-token'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        device_token: fcmToken,
        device_type: getDeviceType(),
      }),
    });
    if (response.ok) return true;
    console.error('[Push] Erreur enregistrement token:', response.status);
    return false;
  } catch (error) {
    console.error('[Push] Erreur réseau enregistrement token:', error);
    return false;
  }
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * iOS : attend la fin de l'enregistrement APNs (événement `registration`),
 * donne 3s à Firebase pour l'échange APNs→FCM, puis récupère le token FCM.
 * Retente jusqu'à 10 fois × 1s.
 */
async function fetchIosFcmTokenAndRegister(forceRegister = false): Promise<boolean> {
  await waitForApnsRegistration();
  await sleep(3000);

  for (let attempt = 0; attempt < 10; attempt++) {
    try {
      const { token } = await FcmToken.getToken();
      if (token) {
        const prev = localStorage.getItem('fcm_token');
        if (forceRegister || prev !== token) {
          localStorage.setItem('fcm_token', token);
        }
        return await sendTokenToBackend(token);
      }
    } catch (e) {
      console.warn(`[Push] FCM getToken tentative ${attempt + 1} erreur:`, e);
    }
    await sleep(1000);
  }
  console.error('[Push] FCM token introuvable après 10 tentatives');
  return false;
}

/** Attend que l'événement `registration` Capacitor soit émis (max 10s). */
function waitForApnsRegistration(): Promise<void> {
  if (apnsRegistrationResolver === null) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const original = apnsRegistrationResolver;
    apnsRegistrationResolver = () => {
      original?.();
      resolve();
    };
    setTimeout(resolve, 10000);
  });
}

/** Résout un tap de notification vers un chemin applicatif natif. */
function handleNotificationAction(action: PushNotificationActionPerformed): void {
  const data = action.notification.data as Record<string, string | undefined> | undefined;
  if (!data) return;

  if (data.path) {
    navigateTo(String(data.path));
    return;
  }
  if (data.type === 'new_message') {
    const sid = data.conversation_sid;
    navigateTo(sid ? `/messaging?conversation=${sid}` : '/messaging');
    return;
  }
  if (data.booking_id) {
    navigateTo('/reservations');
    return;
  }
  if (data.type === 'quote_received' || data.quote_id) {
    navigateTo('/quotes');
  }
}

async function attachPushListeners(): Promise<void> {
  if (listenersAttached) return;
  listenersAttached = true;

  const PushNotifications = await getPush();

  apnsRegistrationResolver = () => {
    apnsRegistrationResolver = null;
  };

  await PushNotifications.addListener('registration', async (token: Token) => {
    if (apnsRegistrationResolver) {
      const resolve = apnsRegistrationResolver;
      apnsRegistrationResolver = null;
      resolve();
    }

    if (Capacitor.getPlatform() === 'android') {
      const previousToken = localStorage.getItem('fcm_token');
      const force = pendingAndroidForceSendToken;
      pendingAndroidForceSendToken = false;
      if (force || previousToken !== token.value) {
        localStorage.setItem('fcm_token', token.value);
        await sendTokenToBackend(token.value);
      }
    }
    // iOS : token APNs ici ; FCM géré dans fetchIosFcmTokenAndRegister.
  });

  await PushNotifications.addListener('registrationError', (error: { message: string }) => {
    console.error('[Push] Erreur enregistrement APNs:', error);
    if (apnsRegistrationResolver) {
      const resolve = apnsRegistrationResolver;
      apnsRegistrationResolver = null;
      resolve();
    }
  });

  await PushNotifications.addListener(
    'pushNotificationReceived',
    (notification: PushNotificationSchema) => {
      // Foreground : émettre un évènement DOM pour un toast in-app (voir T5.1).
      window.dispatchEvent(
        new CustomEvent('native-push-received', { detail: notification }),
      );
    },
  );

  await PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (action: PushNotificationActionPerformed) => {
      handleNotificationAction(action);
    },
  );
}

/**
 * Au login / restauration session : aucune popup.
 * Si la permission est déjà accordée, enregistre le token.
 */
export async function syncPushTokenIfAlreadyEnabled(): Promise<void> {
  if (!isPushSupported()) return;

  try {
    const PushNotifications = await getPush();
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== 'granted') return;

    await attachPushListeners();
    if (Capacitor.getPlatform() === 'android') {
      pendingAndroidForceSendToken = true;
    }
    await PushNotifications.register();

    if (Capacitor.getPlatform() === 'ios') {
      fetchIosFcmTokenAndRegister(true).catch((e) =>
        console.error('[Push] Erreur sync token iOS:', e),
      );
    }
  } catch (error) {
    console.error('[Push] syncPushTokenIfAlreadyEnabled:', error);
  }
}

export type PushPermissionResult =
  | { ok: true }
  | {
      ok: false;
      reason:
        | 'not_native'
        | 'denied'
        | 'not_granted'
        | 'ios_fcm_failed'
        | 'android_timeout';
    };

/**
 * À appeler depuis un geste utilisateur (bouton) : affiche la popup iOS si besoin.
 */
export async function requestPushPermissionAndRegister(): Promise<PushPermissionResult> {
  if (!isPushSupported()) return { ok: false, reason: 'not_native' };

  try {
    const PushNotifications = await getPush();
    await attachPushListeners();

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive === 'denied') return { ok: false, reason: 'denied' };
    if (perm.receive !== 'granted') return { ok: false, reason: 'not_granted' };

    if (Capacitor.getPlatform() === 'android') {
      pendingAndroidForceSendToken = true;
    }
    await PushNotifications.register();

    if (Capacitor.getPlatform() === 'ios') {
      const ok = await fetchIosFcmTokenAndRegister(true);
      return ok ? { ok: true } : { ok: false, reason: 'ios_fcm_failed' };
    }

    for (let i = 0; i < 60; i++) {
      await sleep(200);
      if (localStorage.getItem('fcm_token')) return { ok: true };
    }
    return { ok: false, reason: 'android_timeout' };
  } catch (error) {
    console.error('[Push] requestPushPermissionAndRegister:', error);
    return { ok: false, reason: 'not_granted' };
  }
}

/** Retourne l'état courant de la permission push (natif uniquement). */
export async function getPushPermissionState(): Promise<
  'prompt' | 'prompt-with-rationale' | 'granted' | 'denied' | 'unsupported'
> {
  if (!isPushSupported()) return 'unsupported';
  try {
    const PushNotifications = await getPush();
    const perm = await PushNotifications.checkPermissions();
    return perm.receive;
  } catch {
    return 'denied';
  }
}

/**
 * Désinscrit le token FCM courant côté serveur.
 * À appeler au logout AVANT de supprimer le JWT (ou avec les valeurs capturées).
 */
export const unregisterPushTokenOnLogout = async (
  fcmToken?: string | null,
  authToken?: string | null,
): Promise<void> => {
  if (!isPushSupported()) return;

  const token = fcmToken ?? localStorage.getItem('fcm_token');
  const jwt = authToken ?? localStorage.getItem('token');
  if (token && jwt) {
    await deactivateTokenOnBackend(token, jwt);
  }
};

export const cleanupPushNotifications = async (): Promise<void> => {
  if (!isPushSupported()) return;

  try {
    const PushNotifications = await getPush();
    await PushNotifications.removeAllListeners();
    listenersAttached = false;
    localStorage.removeItem('fcm_token');
    apnsRegistrationResolver = null;
  } catch (error) {
    console.error('[Push] Erreur cleanup:', error);
  }
};
